// src/modules/pedidos/components/KitVinculo.js
//
// Muestra, para un renglón que es KIT, la posibilidad de vincular el "pedido de
// componentes" (los que los vendedores marcan con "COMPONENTES KIT" en la
// descripción) y, una vez vinculado, un desplegable con los componentes y sus
// cantidades — para que el operario pueda controlarlos.

import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, doc, updateDoc, getDoc, deleteField } from "firebase/firestore";
import { db } from "../../../firebase";

const MARCADOR = "COMPONENTES KIT";

function textoDescripcion(p) {
  return [p.descripcion, p.observaciones, p.nota, p.comentario, p.detalle].filter(Boolean).join(" ").toUpperCase();
}
function qtyDe(it) {
  return it.cant ?? it.cantidad ?? it.qty ?? 0;
}
function nombreDe(it) {
  return it.descripcion || it.desc || it.nombre || it.cod || it.codigo || "—";
}

export default function KitVinculo({ pedido, codigoKit, puedeVincular, descripcionKit }) {
  const vinculoManual = pedido?.kitsVinculados?.[codigoKit] || null;
  const [autoVinculo, setAutoVinculo] = useState(null); // detectado desde la descripción del kit
  const vinculo = vinculoManual || autoVinculo;
  const esAuto = !vinculoManual && !!autoVinculo;
  const [abierto, setAbierto]     = useState(false);
  const [picker, setPicker]       = useState(false);
  const [candidatos, setCandidatos] = useState(null);
  const [compProductos, setCompProductos] = useState(null);
  const [busy, setBusy]           = useState(false);

  // Auto-detección del pedido de componentes. Dos caminos (el primero que
  // encuentre gana). No pisa un vínculo manual existente.
  //   1) El vendedor escribió el N° del pedido de componentes en la descripción
  //      del kit (número de 4-6 dígitos) → buscamos ese pedido por número.
  //   2) Existe un pedido con "COMPONENTES KIT" que menciona el N° de ESTE pedido
  //      en su descripción (ej. "COMPONENTES KIT 22562") → lo vinculamos.
  useEffect(() => {
    if (vinculoManual) { setAutoVinculo(null); return; }
    let cancel = false;
    (async () => {
      // ── Camino 1: número dentro de la descripción del kit ──
      const candidatosNum = String(descripcionKit || "").match(/\d{4,6}/g) || [];
      for (const n of candidatosNum) {
        for (const valor of [Number(n), n]) {
          try {
            const snap = await getDocs(query(
              collection(db, "pedidos"),
              where("numero", "==", valor),
              where("deposito", "==", pedido.deposito || "R8"),
            ));
            if (cancel) return;
            const encontrado = snap.docs.find(d => d.id !== pedido.id);
            if (encontrado) { setAutoVinculo({ pedidoId: encontrado.id, numero: encontrado.data().numero || n }); return; }
          } catch (e) { console.error("[kit auto 1]", e); }
        }
      }
      // ── Camino 2: pedido de componentes que referencia el N° de este pedido ──
      const miNumero = String(pedido.numero || "").trim();
      if (miNumero) {
        try {
          const snap = await getDocs(query(collection(db, "pedidos"), where("deposito", "==", pedido.deposito || "R8")));
          if (cancel) return;
          const comp = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .find(p => p.id !== pedido.id
              && textoDescripcion(p).includes(MARCADOR)
              && new RegExp(`\\b${miNumero}\\b`).test(textoDescripcion(p)));
          if (comp) { setAutoVinculo({ pedidoId: comp.id, numero: comp.numero || comp.id }); return; }
        } catch (e) { console.error("[kit auto 2]", e); }
      }
      if (!cancel) setAutoVinculo(null);
    })();
    return () => { cancel = true; };
  }, [vinculoManual, descripcionKit, pedido.deposito, pedido.id, pedido.numero]);

  // Cargar los productos del pedido de componentes vinculado (para el desplegable)
  useEffect(() => {
    if (!vinculo?.pedidoId) { setCompProductos(null); return; }
    let cancel = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "pedidos", vinculo.pedidoId));
        if (!cancel) setCompProductos(snap.exists() ? (snap.data().productos || []) : []);
      } catch { if (!cancel) setCompProductos([]); }
    })();
    return () => { cancel = true; };
  }, [vinculo?.pedidoId]);

  async function abrirPicker() {
    setPicker(true);
    setCandidatos(null);
    try {
      const qy = query(collection(db, "pedidos"), where("deposito", "==", pedido.deposito || "R8"));
      const snap = await getDocs(qy);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.id !== pedido.id && textoDescripcion(p).includes(MARCADOR))
        .sort((a, b) => (b.numero || 0) - (a.numero || 0));
      setCandidatos(list);
    } catch (e) { console.error("[kit] candidatos", e); setCandidatos([]); }
  }

  async function vincular(cand) {
    setBusy(true);
    try {
      await updateDoc(doc(db, "pedidos", pedido.id), {
        [`kitsVinculados.${codigoKit}`]: { pedidoId: cand.id, numero: cand.numero || cand.id },
      });
      setPicker(false);
    } catch (e) { console.error("[kit] vincular", e); alert("No se pudo vincular"); }
    finally { setBusy(false); }
  }

  async function desvincular() {
    if (!window.confirm("¿Quitar el pedido de componentes vinculado?")) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, "pedidos", pedido.id), { [`kitsVinculados.${codigoKit}`]: deleteField() });
    } catch (e) { console.error("[kit] desvincular", e); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: "6px", background: "#faf5ff", border: "1.5px solid #e9d5ff", borderRadius: "10px", padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#7c3aed", background: "#f3e8ff", padding: "2px 8px", borderRadius: "999px" }}>🧩 KIT</span>

        {vinculo ? (
          <>
            <button onClick={() => setAbierto(a => !a)} style={{ flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#6b21a8", fontWeight: 700 }}>
              Componentes: pedido #{vinculo.numero} {esAuto && <span style={{ fontSize: "0.62rem", fontWeight: 700, color: "#15803d", background: "#dcfce7", padding: "1px 6px", borderRadius: "999px" }}>auto</span>} {abierto ? "▲" : "▼"}
            </button>
            {puedeVincular && (
              esAuto
                ? <button onClick={abrirPicker} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", color: "#6b21a8", background: "none", border: "1px solid #e9d5ff", borderRadius: "6px", padding: "3px 7px", cursor: "pointer" }}>Cambiar</button>
                : <button onClick={desvincular} disabled={busy} style={{ flexShrink: 0, fontSize: "0.68rem", color: "#dc2626", background: "none", border: "1px solid #fecaca", borderRadius: "6px", padding: "3px 7px", cursor: "pointer" }}>Quitar</button>
            )}
          </>
        ) : puedeVincular ? (
          <button onClick={abrirPicker} disabled={busy} style={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff", background: "#7c3aed", border: "none", borderRadius: "8px", padding: "6px 11px", cursor: "pointer" }}>
            🧩 Vincular componentes
          </button>
        ) : (
          <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>Sin componentes vinculados</span>
        )}
      </div>

      {/* Desplegable de componentes */}
      {vinculo && abierto && (
        <div style={{ marginTop: "8px", borderTop: "1px dashed #e9d5ff", paddingTop: "8px" }}>
          {compProductos === null ? (
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#94a3b8" }}>Cargando componentes…</p>
          ) : compProductos.length === 0 ? (
            <p style={{ margin: 0, fontSize: "0.78rem", color: "#94a3b8" }}>El pedido vinculado no tiene productos.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {compProductos.map((it, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "0.8rem" }}>
                  <span style={{ color: "#334155", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{nombreDe(it)}</span>
                  <span style={{ flexShrink: 0, fontWeight: 700, color: "#7c3aed" }}>×{qtyDe(it)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Picker de pedido de componentes */}
      {picker && (
        <div onClick={() => setPicker(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: "560px", maxHeight: "75vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 16px 8px", borderBottom: "1px solid #e2e8f0" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800 }}>Vincular pedido de componentes</h3>
              <p style={{ margin: "3px 0 0", fontSize: "0.78rem", color: "#64748b" }}>Pedidos con "{MARCADOR}" en la descripción · {pedido.deposito}</p>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
              {candidatos === null ? (
                <p style={{ color: "#94a3b8", fontSize: "0.85rem" }}>Buscando…</p>
              ) : candidatos.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: "0.85rem" }}>No hay pedidos de componentes. Pedí al vendedor que arme el pedido con "{MARCADOR}" en la descripción.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {candidatos.map(c => (
                    <button key={c.id} onClick={() => vincular(c)} disabled={busy}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", padding: "11px 13px", borderRadius: "10px", border: "1.5px solid #e9d5ff", background: "#faf5ff", cursor: "pointer", textAlign: "left" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "0.88rem", color: "#0f172a" }}>#{c.numero || c.id}</div>
                        <div style={{ fontSize: "0.76rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.cliente || "—"} · {(c.productos || []).length} productos</div>
                      </div>
                      <span style={{ flexShrink: 0, color: "#7c3aed", fontWeight: 700, fontSize: "0.82rem" }}>Vincular →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding: "10px 16px 18px" }}>
              <button onClick={() => setPicker(false)} style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, color: "#475569", cursor: "pointer" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
